import { useState } from 'react';
import { StaticPageLayout } from '@/components/StaticPageLayout';
import { Mail, Linkedin, Globe, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type BoardMember = {
  id: number;
  name: string;
  role: string;
  bio: string;
  image: string;
  email?: string;
  linkedin?: string;
  website?: string;
  category: 'executive' | 'pre_grad' | 'pre_health';
};

type Props = {
  board_members: BoardMember[];
};

const categoryDisplayNames = {
  executive: 'Executive Board',
  pre_grad: 'Pre-Grad Directors',
  pre_health: 'Pre-Health Directors',
};

export default function ExecutiveBoard({ board_members }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<
    'all' | 'executive' | 'pre_grad' | 'pre_health'
  >('all');
  const [selectedMember, setSelectedMember] = useState<BoardMember | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = [
    { id: 'all', label: 'All Members', count: board_members.length },
    {
      id: 'executive',
      label: 'Executive Board',
      count: board_members.filter((m) => m.category === 'executive').length,
    },
    {
      id: 'pre_grad',
      label: 'Pre-Grad Directors',
      count: board_members.filter((m) => m.category === 'pre_grad').length,
    },
    {
      id: 'pre_health',
      label: 'Pre-Health Directors',
      count: board_members.filter((m) => m.category === 'pre_health').length,
    },
  ];

  const filteredMembers =
    selectedCategory === 'all'
      ? board_members
      : board_members.filter((member) => member.category === selectedCategory);

  return (
    <StaticPageLayout>
      {/* Hero Section */}
      <section className="bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="py-12 md:py-20">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-sm uppercase tracking-widest text-primary mb-4">
                  LEADERSHIP
                </p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6">
                  The minds
                  <span className="italic"> behind</span>
                  <br />
                  <span className="font-semibold">the mission</span>
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed max-w-2xl">
                  Our board consists of current students and recent graduates
                  who understand firsthand the challenges of navigating
                  professional school applications.
                </p>
              </div>

              {/* Creative Visual - Network of Connections */}
              <div className="relative h-[400px] hidden lg:block">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 400 400"
                  className="absolute inset-0"
                >
                  {/* Central hub */}
                  <circle
                    cx="200"
                    cy="200"
                    r="40"
                    fill="rgb(147 51 234)"
                    opacity="0.05"
                  />
                  <circle
                    cx="200"
                    cy="200"
                    r="25"
                    fill="rgb(147 51 234)"
                    opacity="0.08"
                  />
                  <circle
                    cx="200"
                    cy="200"
                    r="15"
                    fill="white"
                    stroke="rgb(147 51 234)"
                    strokeWidth="2"
                    opacity="0.8"
                  />

                  {/* Orbiting nodes representing board members */}
                  {[...Array(9)].map((_, i) => {
                    const angle = (i * 40 * Math.PI) / 180;
                    const radius = 120 + (i % 3) * 40;
                    const x = 200 + Math.cos(angle) * radius;
                    const y = 200 + Math.sin(angle) * radius;
                    const nodeSize = 8 + (i % 3) * 4;

                    return (
                      <g key={i}>
                        {/* Connection line */}
                        <line
                          x1="200"
                          y1="200"
                          x2={x}
                          y2={y}
                          stroke="rgb(147 51 234)"
                          strokeWidth="0.5"
                          opacity="0.1"
                          strokeDasharray="2,4"
                          className="animate-pulse"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />

                        {/* Outer glow */}
                        <circle
                          cx={x}
                          cy={y}
                          r={nodeSize + 8}
                          fill="rgb(147 51 234)"
                          opacity="0.03"
                          className="animate-pulse"
                          style={{ animationDelay: `${i * 0.3}s` }}
                        />

                        {/* Node */}
                        <circle
                          cx={x}
                          cy={y}
                          r={nodeSize}
                          fill="white"
                          stroke="rgb(147 51 234)"
                          strokeWidth="1.5"
                          opacity="0.8"
                        />

                        {/* Inner dot */}
                        <circle
                          cx={x}
                          cy={y}
                          r="2"
                          fill="rgb(147 51 234)"
                          opacity="0.4"
                        />

                        {/* Secondary connections */}
                        {i % 3 === 0 && (
                          <>
                            <line
                              x1={x}
                              y1={y}
                              x2={x + 30}
                              y2={y - 20}
                              stroke="rgb(147 51 234)"
                              strokeWidth="0.5"
                              opacity="0.05"
                            />
                            <circle
                              cx={x + 30}
                              cy={y - 20}
                              r="3"
                              fill="rgb(147 51 234)"
                              opacity="0.1"
                            />
                          </>
                        )}
                      </g>
                    );
                  })}

                  {/* Floating connections */}
                  {[...Array(20)].map((_, i) => (
                    <circle
                      key={`dot-${i}`}
                      cx={100 + Math.random() * 200}
                      cy={100 + Math.random() * 200}
                      r="1"
                      fill="rgb(147 51 234)"
                      opacity={0.1 + Math.random() * 0.2}
                      className="animate-twinkle"
                      style={{
                        animationDelay: `${Math.random() * 5}s`,
                        animationDuration: `${3 + Math.random() * 4}s`,
                      }}
                    />
                  ))}

                  {/* Ripple effects from center */}
                  {[...Array(3)].map((_, i) => (
                    <circle
                      key={`ripple-${i}`}
                      cx="200"
                      cy="200"
                      r="50"
                      fill="none"
                      stroke="rgb(147 51 234)"
                      strokeWidth="0.5"
                      opacity="0"
                      className="animate-ripple"
                      style={{ animationDelay: `${i * 2}s` }}
                    />
                  ))}
                </svg>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
                  <p className="text-xs text-gray-400">
                    Connected by purpose, united in mission
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-t border-gray-200">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="flex overflow-x-auto py-6 gap-8 no-scrollbar">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id as any)}
                className={`flex items-center gap-3 whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'text-primary'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="text-lg font-light">{category.label}</span>
                <span
                  className={`text-sm ${
                    selectedCategory === category.id
                      ? 'text-primary/60'
                      : 'text-gray-400'
                  }`}
                >
                  ({category.count})
                </span>
                {selectedCategory === category.id && (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Board Members Grid */}
      <section className="py-7">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredMembers.map((member, index) => (
              <div
                key={index}
                className="group relative cursor-pointer"
                onClick={() => {
                  setSelectedMember(member);
                  setIsModalOpen(true);
                }}
              >
                {/* Card content */}
                <div className="relative bg-white p-6 border border-gray-200 hover:border-primary/30 transition-all duration-300">
                  {/* Profile picture */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 group-hover:border-primary/30 transition-colors flex-shrink-0 bg-gray-50">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        {member.name}
                      </h3>
                      <p className="text-xs text-primary uppercase tracking-wider">
                        {member.role}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {member.bio}
                  </p>

                  {/* See more link */}
                  <button className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1">
                    Learn more
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Hover effect line */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/20 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join the Board CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
            Interested in joining us?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            We're always looking for passionate individuals who share our
            commitment to educational equity.
          </p>
          <a
            href="mailto:contact@project-short.com"
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
          >
            <span className="text-lg">Contact us about opportunities</span>
            <ChevronRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>

      {/* Member Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl">
          {selectedMember && (
            <>
              <DialogHeader className="sr-only">
                <DialogTitle>
                  {selectedMember.name} - {selectedMember.role}
                </DialogTitle>
              </DialogHeader>

              <div className="p-2">
                {/* Header section with image and basic info */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-8">
                  <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-gray-200 flex-shrink-0 bg-gray-50">
                    <img
                      src={selectedMember.image}
                      alt={selectedMember.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="text-center sm:text-left flex-1">
                    <h3 className="text-2xl font-light text-gray-900 mb-1">
                      {selectedMember.name}
                    </h3>
                    <p className="text-base text-primary mb-4">
                      {selectedMember.role}
                    </p>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/5 rounded-full">
                      <span className="text-sm text-primary">
                        {categoryDisplayNames[selectedMember.category]}
                      </span>
                    </div>
                  </div>

                  {/* Contact links - moved to top right */}
                  {(selectedMember.email ||
                    selectedMember.linkedin ||
                    selectedMember.website) && (
                    <div className="flex gap-3">
                      {selectedMember.email && (
                        <a
                          href={`mailto:${selectedMember.email}`}
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary/10 hover:text-primary transition-all"
                          aria-label="Email"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      )}
                      {selectedMember.linkedin && (
                        <a
                          href={selectedMember.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary/10 hover:text-primary transition-all"
                          aria-label="LinkedIn"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {selectedMember.website && (
                        <a
                          href={selectedMember.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary/10 hover:text-primary transition-all"
                          aria-label="Website"
                        >
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Bio section */}
                <div className="space-y-6">
                  {selectedMember.bio && (
                    <div>
                      <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500 mb-3">
                        About
                      </h4>
                      <p className="text-gray-700 leading-relaxed text-base">
                        {selectedMember.bio}
                      </p>
                    </div>
                  )}

                  {/* Additional info section */}
                  {/* <div className="pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium uppercase tracking-wider text-gray-500 mb-3">
                      Role at Project SHORT
                    </h4>
                    <p className="text-gray-700 leading-relaxed text-base">
                      As a member of our{' '}
                      {categoryDisplayNames[
                        selectedMember.category
                      ].toLowerCase()}
                      , {selectedMember.name.split(' ')[0]} plays a crucial role
                      in Project SHORT's mission to democratize access to higher
                      education. Their dedication to mentorship and educational
                      equity helps create opportunities for students from all
                      backgrounds.
                    </p>
                  </div> */}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </StaticPageLayout>
  );
}
